import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { FileCsv, ArrowCounterClockwise, CaretDown, CaretUp } from "@phosphor-icons/react";
import api, { formatApiError } from "../api";
import { BrutalButton } from "./Brutal";

/**
 * Recent product-import audit panel for InventoryPage.
 *
 * Mirrors `ExpenseImports` but the undo flow has an extra wrinkle:
 *  - products that have already been consigned / sold / adjusted are SKIPPED
 *    (not deleted) and reported back so the user knows what's still there.
 *
 * Props:
 *  - refreshKey: any  — bump after a fresh import to force re-fetch
 *  - onAfterUndo:     — parent re-loads the product list after undo
 */
export default function ProductImports({ refreshKey, onAfterUndo }) {
  const { t, i18n } = useTranslation();
  const [imports, setImports] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/products/imports", { params: { limit: 20 } });
      setImports(data);
    } catch (e) {
      // soft fail — audit panel is non-critical
    }
  };
  useEffect(() => { load(); }, [refreshKey]);

  const active = imports.filter((r) => !r.undone_at);
  if (active.length === 0) return null;

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString(i18n.language === "ru" ? "ru-RU" : "en-US", {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });

  const undo = async (row) => {
    if (!window.confirm(t("product_imports.confirm_undo"))) return;
    setBusyId(row.id);
    try {
      const { data } = await api.post(`/products/imports/${row.id}/undo`);
      if (data.skipped_count > 0) {
        const names = (data.skipped || []).map((s) => s.name).slice(0, 3).join(", ");
        toast.success(
          t("product_imports.undo_partial", {
            count: data.deleted_count,
            skipped: data.skipped_count,
          }),
          { description: names, duration: 8000 }
        );
      } else {
        toast.success(t("product_imports.undo_success", { count: data.deleted_count }));
      }
      await load();
      if (typeof onAfterUndo === "function") onAfterUndo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section
      className="bg-white border border-zinc-200 rounded-xl overflow-hidden"
      data-testid="product-imports-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-zinc-50 transition-colors"
        data-testid="product-imports-toggle"
      >
        <div className="flex items-center gap-2 min-w-0">
          <FileCsv size={18} weight="bold" className="text-zinc-500 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-zinc-900 truncate">
              {t("product_imports.title")}
            </div>
            <div className="text-xs text-zinc-500 truncate">
              {open
                ? t("product_imports.subtitle")
                : t("product_imports.show", { n: active.length })}
            </div>
          </div>
        </div>
        {open
          ? <CaretUp size={14} weight="bold" className="text-zinc-400" />
          : <CaretDown size={14} weight="bold" className="text-zinc-400" />}
      </button>

      {open && (
        <ul className="border-t border-zinc-200 divide-y divide-zinc-100">
          {active.map((row) => (
            <li
              key={row.id}
              className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3"
              data-testid={`product-import-${row.id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-zinc-900 truncate" title={row.filename}>
                  {row.filename}
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">
                  {fmtDate(row.created_at)} · {t("product_imports.by", { name: row.user_name || row.user_email })}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 self-end sm:self-auto">
                <span
                  className="text-sm tabular-nums text-zinc-900 font-semibold"
                  data-testid={`product-import-amount-${row.id}`}
                >
                  {t("product_imports.count", {
                    count: row.imported_count,
                    units: row.total_units,
                  })}
                </span>
                <BrutalButton
                  size="sm"
                  variant="secondary"
                  onClick={() => undo(row)}
                  disabled={busyId === row.id}
                  data-testid={`product-import-undo-${row.id}`}
                  title={t("product_imports.undo")}
                  className="!border-rose-300 !text-rose-700 hover:!bg-rose-50"
                >
                  <ArrowCounterClockwise size={12} weight="bold" />
                  <span className="hidden sm:inline ml-1">{t("product_imports.undo")}</span>
                </BrutalButton>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
